import { useModalBulkActionConfirmation } from "../modals/ModalBulkActionConfirmation";
import { useModalBulkActionPreparing } from "../modals/ModalBulkActionPreparing";
import { useModalBulkActionResults } from "../modals/ModalBulkActionResults";
import { useModalForegroundProcessing } from "../modals/ModalForegroundProcessing";
import { BulkActionProgress } from "../types";

export type ForegroundBulkActionConfig<T = void> = {
  actionName: string;
  itemCount: number;
  confirmDescription?: string;
  confirmLabel?: string;
  confirmDestructive?: boolean;
  prepare: () => Promise<T>;
  getItemCount?: (data: T) => number;
  process: (
    data: T,
    onProgress: (progress: BulkActionProgress) => void
  ) => Promise<BulkActionProgress>;
};

export function useForegroundBulkAction() {
  const modalConfirmation = useModalBulkActionConfirmation();
  const modalPreparing = useModalBulkActionPreparing();
  const modalProcessing = useModalForegroundProcessing();
  const modalResults = useModalBulkActionResults();

  return async <T>(config: ForegroundBulkActionConfig<T>) => {
    const confirmResult = await modalConfirmation.open({
      actionName: config.actionName,
      actionType: "foreground",
      itemCount: config.itemCount,
      description: config.confirmDescription,
      confirmLabel: config.confirmLabel,
      confirmDestructive: config.confirmDestructive
    });

    if (!confirmResult) return;

    modalPreparing.open({
      actionName: config.actionName,
      actionType: "foreground",
      itemCount: config.itemCount
    });

    let data: T;
    try {
      data = await config.prepare();
    } finally {
      modalPreparing.close();
    }

    const actualItemCount = config.getItemCount?.(data) ?? config.itemCount;

    const result = await modalProcessing.open({
      actionName: config.actionName,
      itemCount: actualItemCount,
      process: onProgress => config.process(data, onProgress)
    });

    if (!result) return;

    await modalResults.open({
      actionName: config.actionName,
      result
    });
  };
}
